import { ShopflowUserAgent } from './plugin';
import { isAndroidPluginAvailable } from '../utils';

const PLUGIN_NAME = 'ShopflowUserAgent';

type SetOptions = {
  reload_on_change?: boolean;
};

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const removeMarkerSegment = (user_agent: string, name: string) => {
  const marker_pattern = new RegExp(`(?:^|\\s)${escapeRegExp(name)}\\s*\\([^)]*\\)`, 'g');

  return user_agent.replace(marker_pattern, ' ').trim();
};

const buildSuffix = (name: string, value: string) => `${name} (${value})`.trim();

const buildUserAgent = (user_agent: string, suffix: string) => `${user_agent} ${suffix}`.trim();

export const useUserAgent = () => {
  /**
   * Получить текущий user-agent WebView.
   *
   * @returns Текущая строка user-agent.
   */
  const get = async () => {
    if (!isAndroidPluginAvailable(PLUGIN_NAME)) return null;

    const { user_agent } = await ShopflowUserAgent.get();
    
    return user_agent;
  };

  /**
   * Установить новый user-agent, только если он отличается от текущего.
   *
   * @param current_user_agent Текущее значение user-agent.
   * @param next_user_agent Новое значение user-agent для установки.
   * @param options Дополнительные опции плагина.
   */
  const set = async (current_user_agent: string, next_user_agent: string,  options: SetOptions = {}) => {
    if (!isAndroidPluginAvailable(PLUGIN_NAME)) return;

    if (next_user_agent !== current_user_agent) {
      await ShopflowUserAgent.set({
        user_agent: next_user_agent,
        ...options,
      });
    }
  };

  /**
   * Сбросить user-agent WebView к значению по умолчанию платформы.
   */
  const reset = async () => {
    if (!isAndroidPluginAvailable(PLUGIN_NAME)) return;

    await ShopflowUserAgent.reset();
  };

  /**
   * Добавить сегмент `name (value)` в конец user-agent.
   *
   * @param name Имя сегмента.
   * @param value Значение сегмента.
   * @param options Дополнительные опции плагина.
   * @param user_agent Необязательное заранее полученное текущее значение user-agent.
   * @returns Обновленная строка user-agent.
   */
  const append = async (name: string, value: string, options: SetOptions = {}, user_agent: string = null) => {
    if (!isAndroidPluginAvailable(PLUGIN_NAME)) return null;

    const current_user_agent = user_agent || await get();

    const suffix = buildSuffix(name, value);
    const next_user_agent = buildUserAgent(current_user_agent, suffix);

    await set(current_user_agent, next_user_agent, options);

    return next_user_agent;
  };

  /**
   * Заменить существующий сегмент `name (...)` на `name (value)`.
   * Если сегмент не найден, он будет добавлен в конец.
   *
   * @param name Имя сегмента.
   * @param value Значение сегмента.
   * @param options Дополнительные опции плагина.
   * @param user_agent Необязательное заранее полученное текущее значение user-agent.
   * @returns Обновленная строка user-agent.
   */
  const replace = async (name: string, value: string, options: SetOptions = {}, user_agent: string = null) => {
    if (!isAndroidPluginAvailable(PLUGIN_NAME)) return null;

    const current_user_agent = user_agent || await get();

    const suffix = buildSuffix(name, value);
    const base_user_agent = removeMarkerSegment(current_user_agent, name);
    const next_user_agent = buildUserAgent(base_user_agent, suffix);

    await set(current_user_agent, next_user_agent, options);

    return next_user_agent;
  };

  /**
   * Добавить сегмент, если его нет, иначе заменить существующий по имени.
   *
   * @param name Имя сегмента.
   * @param value Значение сегмента.
   * @param options Дополнительные опции плагина.
   * @returns Текущая или обновленная строка user-agent.
   */
  const appendOrReplace = async (name: string, value: string, options: SetOptions = {}) => {
    if (!isAndroidPluginAvailable(PLUGIN_NAME)) return null;

    const normalized_name = name.trim();
    const normalized_value = value.trim();
    const current_user_agent = await get();
    
    if (!normalized_name || !normalized_value) return current_user_agent;

    return current_user_agent.includes(normalized_name)
      ? replace(normalized_name, normalized_value, options, current_user_agent)
      : append(normalized_name, normalized_value, options, current_user_agent);
  };

  return {
    get,
    set,
    reset,
    append,
    replace,
    appendOrReplace,
  };
};
